import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTextModeration } from '@/hooks/useTextModeration';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Loader2, AlertCircle, Camera, User, Check, X, ArrowLeft, RotateCcw, Sparkles, Users, Download, Trash2, Cookie, UserPlus, Lock, KeyRound, Mail } from 'lucide-react';
import { DinoAvatar } from '@/components/DinoAvatar';
import { AvatarEditor } from '@/components/AvatarEditor';
import { useUsernameCheck } from '@/hooks/useUsernameCheck';
import { PrivacyModeSelector } from '@/components/PrivacyModeSelector';
import { InviteFriendsDialog } from '@/components/InviteFriendsDialog';
import { FindFriendsDialog } from '@/components/FindFriendsDialog';
import { BlockedUsersList } from '@/components/BlockedUsersList';
import { FollowRequestsList } from '@/components/FollowRequestsList';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { getUserDefaultFormat, READING_FORMAT_LABELS, ReadingFormat } from '@/lib/readingFormatUtils';
import { ThemeSettings } from '@/components/ThemeSettings';

// Profile data interface
interface ProfileData {
  id: string;
  username?: string;
  name?: string;
  bio?: string;
  image?: string;
  image_thumb?: string;
  email?: string;
  privacy_mode?: 'public' | 'private' | 'ghost';
}

function Settings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { moderateText, isChecking: isModeratingText } = useTextModeration();
  
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAvatarEditorOpen, setIsAvatarEditorOpen] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [inviteFriendsOpen, setInviteFriendsOpen] = useState(false);
  const [findFriendsOpen, setFindFriendsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

// Cookie consent
const { preferences, updatePreferences, acceptAll, rejectAll } = useCookieConsent();

// Reading format preference display
const [defaultReadingFormat, setDefaultReadingFormat] = useState<ReadingFormat | null>(null);
const loadReadingFormat = useCallback(async () => {
  if (!user) return;
  try {
    const fmt = await getUserDefaultFormat(user.id);
    setDefaultReadingFormat(fmt);
  } catch {
    setDefaultReadingFormat(null);
  }
}, [user]);

useEffect(() => {
  loadReadingFormat();
}, [loadReadingFormat]);

// Reload format when returning to settings page
useEffect(() => {
  if (location.pathname === '/settings') {
    loadReadingFormat();
  }
}, [location.pathname, loadReadingFormat]);

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    bio: '',
    privacyMode: 'public' as 'public' | 'private' | 'ghost'
  });

  // Username availability check
  const { isAvailable: isUsernameAvailable, isChecking, error: usernameError } = useUsernameCheck(
    formData.username,
    profile?.id
  );

  // Load profile data with fallback creation
  const loadProfile = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      let { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      // If no profile exists, create one
      if (!data && !error) {
        const defaultUsername = await generateUniqueUsername();
        const { data: newProfile, error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            name: user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || '',
            username: defaultUsername
          })
          .select()
          .single();
          
        if (insertError) {
          console.error('Profile creation error:', insertError);
          setError('Failed to create profile. Please try again.');
          return;
        }
        data = newProfile;
      }

      if (error) {
        console.error('Profile load error:', error);
        setError('Failed to load profile. Please try again.');
        return;
      }

      setProfile(data);
      setFormData({
        username: data.username || '',
        name: data.name || '',
        bio: data.bio || '',
        privacyMode: data.privacy_mode || 'public'
      });
    } catch (error) {
      console.error('Unexpected error loading profile:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Generate unique username helper
  const generateUniqueUsername = async () => {
    try {
      const { data } = await supabase.rpc('generate_unique_username', {
        base_name: user?.email?.split('@')[0] || 'reader'
      });
      return data || `reader${Math.floor(Math.random() * 10000)}`;
    } catch (error) {
      console.error('Username generation error:', error);
      return `reader${Math.floor(Math.random() * 10000)}`;
    }
  };

  // Form validation
  const validateForm = () => {
    const errors: Record<string, string> = {};

    // Username validation
    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    } else if (formData.username.length > 20) {
      errors.username = 'Username must be 20 characters or less';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      errors.username = 'Username can only contain letters, numbers, and underscores';
    }

    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Display name is required';
    } else if (formData.name.length > 100) {
      errors.name = 'Display name must be 100 characters or less';
    }

    // Bio validation
    if (formData.bio && formData.bio.length > 500) {
      errors.bio = 'Bio must be 500 characters or less';
    }

    return errors;
  };

  // Check username availability
  const checkUsernameAvailability = async (username: string) => {
    if (!user || !profile || username === profile.username) return true;
    
    try {
      const { data } = await supabase.rpc('is_username_available', {
        check_username: username,
        current_user_id: user.id
      });
      return data;
    } catch (error) {
      console.error('Username check error:', error);
      return false;
    }
  };

  // Handle save
  const handleSaveProfile = useCallback(async () => {
    if (!user) return;

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Check username availability if changed
    if (profile && formData.username !== profile.username) {
      const available = await checkUsernameAvailability(formData.username);
      if (!available) {
        setFormErrors({ username: 'Username is already taken' });
        return;
      }
    }

    // Moderate bio if changed
    if (formData.bio && formData.bio.trim() && formData.bio !== profile?.bio) {
      const isApproved = await moderateText(formData.bio.trim());
      if (!isApproved) {
        setSaving(false);
        return;
      }
    }

    try {
      setSaving(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('profiles')
        .update({
          username: formData.username,
          name: formData.name,
          bio: formData.bio,
          privacy_mode: formData.privacyMode,
        })
        .eq('id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Profile save error:', error);
        setError('Failed to save profile. Please try again.');
        return;
      }

      // Update local state
      setProfile(data);

      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated."
      });
    } catch (error) {
      console.error('Unexpected save error:', error);
      setError('An unexpected error occurred while saving.');
    } finally {
      setSaving(false);
    }
  }, [user, profile, formData, toast]);

  // Handle avatar update
  const handleAvatarUpdated = useCallback((newAvatarUrl: string, thumbnailUrl?: string) => {
    setProfile(prev => prev ? { 
      ...prev, 
      image: newAvatarUrl,
      image_thumb: thumbnailUrl || newAvatarUrl
    } : null);
  }, []);

  // Handle data export
  const handleExportData = useCallback(async () => {
    if (!user) return;

    try {
      setIsExporting(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Error",
          description: "You must be logged in to export data",
          variant: "destructive"
        });
        return;
      }

      const response = await fetch(
        `https://zjubnjpiajoscjmogyqb.supabase.co/functions/v1/export-user-data`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to export data');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dino-data-${user.id}-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Data exported",
        description: "Your data has been downloaded successfully"
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export failed",
        description: "Failed to export your data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  }, [user, toast]);

  // Handle account deletion
  const handleDeleteAccount = useCallback(async () => {
    if (!user || deleteConfirmText !== 'DELETE MY ACCOUNT') {
      toast({
        title: "Invalid confirmation",
        description: "Please type 'DELETE MY ACCOUNT' to confirm",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsDeleting(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Error",
          description: "You must be logged in to delete your account",
          variant: "destructive"
        });
        return;
      }

      const response = await fetch(
        `https://zjubnjpiajoscjmogyqb.supabase.co/functions/v1/delete-user-account`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ confirmationText: deleteConfirmText }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete account');
      }

      toast({
        title: "Account deleted",
        description: "Your account has been permanently deleted"
      });

      // Sign out and redirect
      await supabase.auth.signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: "Deletion failed",
        description: "Failed to delete your account. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
    }
  }, [user, deleteConfirmText, toast, navigate]);

  useEffect(() => {
    if (user) {
      loadProfile();
    } else {
      navigate('/auth');
    }
  }, [loadProfile, user, navigate]);

  // Clear form errors when form data changes
  useEffect(() => {
    setFormErrors({});
  }, [formData]);

  if (!user) {
    return null; // Will redirect in useEffect
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-lg">Loading profile...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto space-y-6">
            <Button 
              variant="ghost" 
              onClick={() => navigate(-1)}
              className="mb-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {error}
              </AlertDescription>
            </Alert>
            <Button onClick={loadProfile} variant="outline">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-heading font-bold">Profile Settings</h1>
          </div>
          {/* Avatar Section */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>Profile Picture</CardTitle>
              <CardDescription>
                Choose an avatar or upload your own photo
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-6">
                <DinoAvatar 
                  src={profile?.image} 
                  name={profile?.name || profile?.username}
                  size="xl"
                  className="ring-2 ring-primary/20"
                />
                
                <div className="space-y-2">
                  <Button 
                    onClick={() => setIsAvatarEditorOpen(true)}
                    className="flex items-center gap-2"
                  >
                    <Camera className="w-4 h-4" />
                    {profile?.image ? 'Change Avatar' : 'Upload Avatar'}
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    JPG, PNG or WebP. Max 5MB. Photos will be cropped to square.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Profile Information */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>
                This information will be visible on your public profile
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Username */}
              <div className="space-y-2">
                <Label htmlFor="username">Username *</Label>
                <div className="relative">
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="Enter your username"
                    className={formErrors.username || (formData.username && formData.username !== profile?.username && isUsernameAvailable === false) ? 'border-destructive' : ''}
                  />
                  {isChecking && (
                    <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin" />
                  )}
                  {formData.username && formData.username !== profile?.username && !isChecking && (
                    <div className="absolute right-3 top-3">
                      {isUsernameAvailable ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <X className="h-4 w-4 text-destructive" />
                      )}
                    </div>
                  )}
                </div>
                {formErrors.username && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {formErrors.username}
                  </p>
                )}
                {formData.username && formData.username !== profile?.username && !isChecking && !isUsernameAvailable && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Username is already taken
                  </p>
                )}
                {usernameError && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {usernameError}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">
                  Your public profile will be available at /u/{formData.username || 'username'}
                </p>
              </div>

              {/* Display Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Display Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter your display name"
                  className={formErrors.name ? 'border-destructive' : ''}
                />
                {formErrors.name && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {formErrors.name}
                  </p>
                )}
              </div>

              {/* Email (read-only) */}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  value={user?.email || ''}
                  disabled
                  className="bg-muted"
                />
                <p className="text-sm text-muted-foreground">
                  Your email cannot be changed here. Contact support if needed.
                </p>
              </div>

              {/* Bio */}
              <div className="space-y-2">
                <Label htmlFor="bio">Bio (Optional)</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Tell others about yourself..."
                  rows={4}
                  className={formErrors.bio ? 'border-destructive' : ''}
                />
                {formErrors.bio && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {formErrors.bio}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">
                  {formData.bio.length}/500 characters
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Privacy Mode */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>Privacy Mode</CardTitle>
              <CardDescription>
                Choose how visible you want your profile and activity to be
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PrivacyModeSelector
                value={formData.privacyMode}
                onChange={(mode) => setFormData({ ...formData, privacyMode: mode })}
                disabled={saving}
              />
            </CardContent>
          </Card>

          {/* Theme Settings */}
          <ThemeSettings />

          {/* Security / Password */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Security
              </CardTitle>
              <CardDescription>
                Manage your password and account security
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="font-medium">Password</div>
                    <p className="text-sm text-muted-foreground">
                      Set or change your password to sign in without a magic link
                    </p>
                  </div>
                </div>
                <Button
                  onClick={async () => {
                    if (!user?.email) return;
                    try {
                      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
                        redirectTo: `${window.location.origin}/auth?mode=update-password`,
                      });
                      if (error) throw error;
                      toast({
                        title: "Password reset email sent",
                        description: "Check your inbox to set a new password.",
                      });
                    } catch (error: any) {
                      toast({
                        title: "Error",
                        description: error.message || "Failed to send reset email",
                        variant: "destructive",
                      });
                    }
                  }}
                  variant="outline"
                  className="w-full sm:w-auto flex items-center gap-2"
                >
                  <KeyRound className="w-4 h-4" />
                  Set / Change Password
                </Button>
                <p className="text-xs text-muted-foreground">
                  We'll send you an email to set or change your password securely.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Reading Preferences */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>Reading Preferences</CardTitle>
              <CardDescription>
                Customize your reading experience and set defaults
              </CardDescription>
            </CardHeader>
            <CardContent>
              {defaultReadingFormat && (
                <p className="text-sm text-muted-foreground mb-2">
                  Current default: <span className="font-medium">{READING_FORMAT_LABELS[defaultReadingFormat]}</span>
                </p>
              )}
              <Button
                onClick={() => navigate('/onboarding/reading-format?retake=1&returnTo=/settings')}
                variant="outline"
                className="w-full flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Set Default Reading Format
              </Button>
              <p className="text-sm text-muted-foreground mt-2">
                Choose your preferred reading format (print, digital, or audiobook) which will be pre-selected when rating books
              </p>
            </CardContent>
          </Card>

          {/* Import Books */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>Import Books</CardTitle>
              <CardDescription>
                Import your reading history from Goodreads, or scan photos of your bookshelf
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2">
                <Button
                  onClick={() => navigate('/onboarding/bookshelf-scan')}
                  className="w-full flex items-center gap-2"
                >
                  <Camera className="w-4 h-4" />
                  Scan Bookshelf Photos
                </Button>
                <Button
                  onClick={() => navigate('/import')}
                  variant="outline"
                  className="w-full flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Import from Goodreads
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Reader Archetype Quiz */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>Reader Archetype</CardTitle>
              <CardDescription>
                Retake the quiz to discover or update your reading personality
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => navigate('/onboarding/archetype?retake=1', { replace: true, state: { retake: true } })}
                variant="outline"
                className="w-full flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Retake Archetype Quiz
              </Button>
            </CardContent>
          </Card>

          {/* Follow Requests */}
          <Card className="card-elevated" id="follow-requests">
            <CardHeader>
              <CardTitle>Follow Requests</CardTitle>
              <CardDescription>
                Manage pending follow requests from other users
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FollowRequestsList />
            </CardContent>
          </Card>

          {/* Social & Friends */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>Social & Friends</CardTitle>
              <CardDescription>
                Find friends on Dino or invite new ones to join the reading community
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2">
                <Button
                  onClick={() => setFindFriendsOpen(true)}
                  variant="outline"
                  className="w-full flex items-center gap-2"
                >
                  <Users className="w-4 h-4" />
                  Find Friends
                </Button>
                <Button
                  onClick={() => setInviteFriendsOpen(true)}
                  variant="outline"
                  className="w-full flex items-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Invite Friends
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Blocked Users */}
          <BlockedUsersList />

          {/* Cookie Preferences */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cookie className="w-5 h-5" />
                Cookie Preferences
              </CardTitle>
              <CardDescription>
                Manage how we use cookies to improve your experience
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Necessary Cookies */}
              <div className="flex items-start justify-between gap-4 pb-4 border-b">
                <div className="space-y-1 flex-1">
                  <div className="font-medium">Necessary Cookies</div>
                  <p className="text-sm text-muted-foreground">
                    Required for the app to function. These cannot be disabled.
                  </p>
                </div>
                <Switch
                  checked={true}
                  disabled={true}
                  className="mt-1"
                />
              </div>

              {/* Functional Cookies */}
              <div className="flex items-start justify-between gap-4 pb-4 border-b">
                <div className="space-y-1 flex-1">
                  <div className="font-medium">Functional Cookies</div>
                  <p className="text-sm text-muted-foreground">
                    Enable enhanced features like remembering your preferences and settings.
                  </p>
                </div>
                <Switch
                  checked={preferences.functional}
                  onCheckedChange={(checked) => updatePreferences({ functional: checked })}
                  className="mt-1"
                />
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start justify-between gap-4 pb-4 border-b">
                <div className="space-y-1 flex-1">
                  <div className="font-medium">Analytics Cookies</div>
                  <p className="text-sm text-muted-foreground">
                    Help us understand how you use the app so we can improve your experience.
                  </p>
                </div>
                <Switch
                  checked={preferences.analytics}
                  onCheckedChange={(checked) => updatePreferences({ analytics: checked })}
                  className="mt-1"
                />
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-start justify-between gap-4 pb-4">
                <div className="space-y-1 flex-1">
                  <div className="font-medium">Marketing Cookies</div>
                  <p className="text-sm text-muted-foreground">
                    Used to show you relevant content and advertisements.
                  </p>
                </div>
                <Switch
                  checked={preferences.marketing}
                  onCheckedChange={(checked) => updatePreferences({ marketing: checked })}
                  className="mt-1"
                />
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={acceptAll}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  Accept All
                </Button>
                <Button
                  onClick={rejectAll}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  Reject All
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* GDPR & Data Management */}
          <Card className="card-elevated border-primary/20">
            <CardHeader>
              <CardTitle>Data & Privacy</CardTitle>
              <CardDescription>
                Download your data or permanently delete your account
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <Button
                  onClick={handleExportData}
                  disabled={isExporting}
                  variant="outline"
                  className="w-full flex items-center gap-2"
                >
                  {isExporting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  {isExporting ? 'Exporting...' : 'Download My Data'}
                </Button>
                <p className="text-sm text-muted-foreground">
                  Export all your data including profile, books, reviews, and activity in JSON format
                </p>
              </div>

              <div className="border-t pt-4 space-y-3">
                {!showDeleteConfirm ? (
                  <>
                    <Button
                      onClick={() => setShowDeleteConfirm(true)}
                      variant="destructive"
                      className="w-full flex items-center gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete My Account
                    </Button>
                    <p className="text-sm text-muted-foreground">
                      Permanently delete your account and all associated data. This action cannot be undone.
                    </p>
                  </>
                ) : (
                  <div className="space-y-4 p-4 border border-destructive rounded-lg bg-destructive/5">
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Warning:</strong> This will permanently delete your account, all your books, reviews, ratings, and social connections. This action cannot be undone.
                      </AlertDescription>
                    </Alert>
                    
                    <div className="space-y-2">
                      <Label htmlFor="delete-confirm">
                        Type <strong>DELETE MY ACCOUNT</strong> to confirm
                      </Label>
                      <Input
                        id="delete-confirm"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder="DELETE MY ACCOUNT"
                        className="font-mono"
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={handleDeleteAccount}
                        disabled={isDeleting || deleteConfirmText !== 'DELETE MY ACCOUNT'}
                        variant="destructive"
                        className="flex-1"
                      >
                        {isDeleting ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Trash2 className="h-4 w-4 mr-2" />
                        )}
                        {isDeleting ? 'Deleting...' : 'Permanently Delete Account'}
                      </Button>
                      <Button
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeleteConfirmText('');
                        }}
                        variant="outline"
                        disabled={isDeleting}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card className="card-elevated">
            <CardContent className="pt-6">
              <div className="flex gap-4">
                <Button
                  onClick={handleSaveProfile}
                  disabled={saving || isChecking || (formData.username !== profile?.username && !isUsernameAvailable)}
                  className="flex items-center gap-2"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                
                {profile?.username && (
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/u/${profile.username}`)}
                    className="flex items-center gap-2"
                  >
                    <User className="h-4 w-4" />
                    View Public Profile
                  </Button>
                )}
                
                <Button
                  variant="outline"
                  onClick={() => {
                    sessionStorage.setItem('retakeOnboarding', '1');
                    navigate('/onboarding?retake=1');
                  }}
                  className="flex items-center gap-2"
                >
                  <RotateCcw className="h-4 w-4" />
                  Retake Onboarding
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* First-time setup alert */}
          {!profile?.username && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Complete your profile setup by adding a username to get the most out of Dino.
              </AlertDescription>
            </Alert>
          )}

          {/* Dev diagnostics */}
          {import.meta.env.DEV && (
            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Dev Diagnostics</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                <p>User ID: {user?.id}</p>
                <p>Username: {profile?.username || 'Not set'}</p>
                <p>Last Error: {error || 'None'}</p>
                <p>Profile Loaded: {profile ? 'Yes' : 'No'}</p>
                <p>Username Available: {isUsernameAvailable === null ? 'Unknown' : isUsernameAvailable ? 'Yes' : 'No'}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      <AvatarEditor
        isOpen={isAvatarEditorOpen}
        onClose={() => setIsAvatarEditorOpen(false)}
        currentAvatar={profile?.image}
        onAvatarUpdated={handleAvatarUpdated}
      />

      <InviteFriendsDialog 
        isOpen={inviteFriendsOpen} 
        onClose={() => setInviteFriendsOpen(false)} 
      />
      <FindFriendsDialog 
        isOpen={findFriendsOpen} 
        onClose={() => setFindFriendsOpen(false)} 
      />
    </div>
  );
}

export default Settings;